import type { InsightContent } from "./types";

export const consultantVsAgency: InsightContent = {
  id: "consultant-vs-agency",
  format: "guide",
  cluster: "SEO Strategy",
  moneyPage: { es: "/consultor-seo", en: "/en/seo-consultant" },
  path: {
    es: "/biblioteca/consultor-seo-o-agencia-seo",
    en: "/en/library/seo-consultant-vs-seo-agency",
  },
  published: "2026-10-17",
  updated: "2026-10-17",
  readingMinutes: 7,
  es: {
    title: "Consultor SEO o agencia SEO: cómo elegir",
    h1: "¿Consultor SEO o agencia SEO? Cómo elegir según tu empresa",
    description: "Criterios prácticos para decidir entre un consultor SEO y una agencia: quién ejecuta, quién decide, qué preguntar y cuándo conviene cada modelo.",
    eyebrow: "Estrategia SEO",
    summary: "Un consultor SEO aporta criterio y dirección con una persona responsable de principio a fin; una agencia aporta capacidad de ejecución con un equipo. La elección correcta depende de qué le falta hoy a tu empresa: decidir qué hacer, o tener manos para hacerlo.",
    body: [
      { type: "p", text: "Muchas empresas comparan consultores y agencias por precio o por tamaño, cuando la pregunta útil es otra: ¿el cuello de botella es la estrategia o la ejecución? Si nadie en la empresa sabe qué priorizar, más horas de producción no resuelven el problema. Si la estrategia está clara pero falta capacidad, una sola persona puede quedarse corta." },
      { type: "h2", text: "Diferencias que importan en la práctica" },
      { type: "table", head: ["Criterio", "Consultor SEO", "Agencia SEO"], rows: [
        ["Quién trabaja tu cuenta", "La misma persona que vende el proyecto", "Un equipo; conviene preguntar quién lo integra"],
        ["Fortaleza principal", "Diagnóstico, priorización y criterio de negocio", "Volumen de producción y cobertura de varias disciplinas"],
        ["Capacidad de ejecución", "Limitada a una persona o a una red de apoyo", "Mayor, con roles especializados"],
        ["Comunicación", "Directa con quien toma las decisiones técnicas", "Normalmente a través de un ejecutivo de cuenta"],
        ["Encaje típico", "Empresas con equipo interno o proveedores que necesitan dirección", "Empresas sin equipo propio que necesitan producción continua"],
      ]},
      { type: "h2", text: "¿Qué le falta hoy a tu empresa?" },
      { type: "decision", options: [
        { situation: "Tengo equipo o proveedores, pero falta rumbo y priorización", model: "Consultor SEO", detail: "Una persona responsable define qué hacer primero y por qué, y tu equipo ejecuta." },
        { situation: "La estrategia está definida, pero faltan manos para producir", model: "Agencia SEO", detail: "Un equipo con roles especializados aporta capacidad de producción continua." },
        { situation: "Necesito dirección y también capacidad de ejecución", model: "Modelo combinado", detail: "El consultor gobierna la estrategia y supervisa; un equipo interno o una agencia ejecuta." },
      ]},
      { type: "h2", text: "Cuándo suele encajar mejor un consultor" },
      { type: "list", tone: "fit", items: [
        "Ya tienes equipo de marketing, desarrollo o contenido, pero falta alguien que decida qué hacer primero y por qué.",
        "Vas a enfrentar un evento de riesgo: migración, rediseño, cambio de dominio o entrada a un mercado nuevo.",
        "Necesitas un segundo criterio sobre el trabajo de una agencia actual.",
        "El problema involucra varias áreas a la vez (técnico, contenido, conversión, mercado hispano) y requiere una visión integrada.",
      ]},
      { type: "h2", text: "Cuándo suele encajar mejor una agencia" },
      { type: "list", tone: "fit", items: [
        "No tienes equipo interno y necesitas producción constante de contenido, enlaces o campañas.",
        "La estrategia ya está definida y documentada, y lo que falta es capacidad.",
        "Necesitas cubrir muchas disciplinas al mismo tiempo con plazos cortos.",
      ]},
      { type: "callout", text: "Google advierte que ningún proveedor puede garantizar el primer lugar en sus resultados y recomienda desconfiar de quien lo prometa. Aplica igual a consultores y agencias.", source: { label: "Google Search Central — ¿Necesitas un SEO?", href: "https://developers.google.com/search/docs/fundamentals/do-i-need-seo" } },
      { type: "h2", text: "Preguntas para hacer antes de contratar" },
      { type: "steps", items: [
        "¿Quién, con nombre, va a trabajar mi proyecto cada semana?",
        "¿Qué van a revisar primero y cómo deciden qué priorizar?",
        "¿Qué entregables recibiré y con qué frecuencia?",
        "¿Cómo se mide el avance y qué indicadores no dependen solo del tráfico?",
        "¿Qué necesitan de mi equipo para que el trabajo avance?",
        "¿Qué pasa con los accesos, los datos y el trabajo hecho si terminamos la relación?",
      ]},
      { type: "h2", text: "Señales de alerta en cualquier proveedor" },
      { type: "list", tone: "alert", items: [
        "Garantías de posiciones, tráfico o ventas.",
        "Propuestas idénticas para empresas con situaciones distintas.",
        "Falta de claridad sobre quién hace el trabajo.",
        "Reportes centrados en métricas que no se conectan con el negocio.",
      ]},
      { type: "h2", text: "Un modelo combinado también es válido" },
      { type: "p", text: "En muchas empresas funciona que un consultor defina la dirección y supervise, mientras un equipo interno o una agencia ejecuta. Así la estrategia no depende de quien produce, y la producción tiene un criterio claro detrás." },
    ],
    cta: {
      text: "Si no tienes claro qué modelo necesita tu empresa, la reunión inicial gratuita de 30 minutos sirve para conocer tu situación y definir posibles siguientes pasos.",
      label: "Solicitar reunión inicial gratuita de 30 minutos",
      href: "/diagnostico-seo-gratis",
    },
    related: [
      { label: "Consultor SEO estratégico", href: "/consultor-seo" },
      { label: "¿Auditoría SEO o estrategia SEO?", href: "/biblioteca/auditoria-seo-o-estrategia-seo" },
      { label: "Metodología", href: "/metodologia" },
    ],
  },
  en: {
    title: "SEO Consultant vs SEO Agency: How to Choose",
    h1: "SEO Consultant or SEO Agency? How to Choose for Your Business",
    description: "Practical criteria to choose between an SEO consultant and an agency: who executes, who decides, what to ask and when each model fits.",
    eyebrow: "SEO Strategy",
    summary: "An SEO consultant brings judgment and direction with one person accountable end to end; an agency brings execution capacity with a team. The right choice depends on what your company lacks today: deciding what to do, or having the hands to do it.",
    body: [
      { type: "p", text: "Many companies compare consultants and agencies on price or size, when the useful question is different: is the bottleneck strategy or execution? If nobody in the company knows what to prioritize, more production hours won't fix it. If the strategy is clear but capacity is missing, a single person may fall short." },
      { type: "h2", text: "Differences that matter in practice" },
      { type: "table", head: ["Criterion", "SEO consultant", "SEO agency"], rows: [
        ["Who works your account", "The same person who sells the project", "A team; worth asking who is on it"],
        ["Main strength", "Diagnosis, prioritization and business judgment", "Production volume and coverage across disciplines"],
        ["Execution capacity", "Limited to one person or a support network", "Larger, with specialized roles"],
        ["Communication", "Direct with whoever makes the technical decisions", "Usually through an account manager"],
        ["Typical fit", "Companies with in-house teams or vendors that need direction", "Companies without a team that need ongoing production"],
      ]},
      { type: "h2", text: "What does your company lack today?" },
      { type: "decision", options: [
        { situation: "I have a team or vendors, but no direction or priorities", model: "SEO consultant", detail: "One accountable person decides what comes first and why; your team executes." },
        { situation: "The strategy is defined, but we lack hands to produce", model: "SEO agency", detail: "A team with specialized roles brings ongoing production capacity." },
        { situation: "I need both direction and execution capacity", model: "Combined model", detail: "The consultant governs strategy and oversees; an in-house team or agency executes." },
      ]},
      { type: "h2", text: "When a consultant usually fits better" },
      { type: "list", tone: "fit", items: [
        "You already have marketing, development or content people, but nobody deciding what comes first and why.",
        "You face a risk event: migration, redesign, domain change or entry into a new market.",
        "You need a second opinion on a current agency's work.",
        "The problem spans several areas (technical, content, conversion, US Hispanic market) and needs an integrated view.",
      ]},
      { type: "h2", text: "When an agency usually fits better" },
      { type: "list", tone: "fit", items: [
        "You have no in-house team and need steady production of content, links or campaigns.",
        "The strategy is already defined and documented; what's missing is capacity.",
        "You need to cover many disciplines at once on short timelines.",
      ]},
      { type: "callout", text: "Google warns that no provider can guarantee a #1 ranking and advises caution with anyone who promises it. That applies to consultants and agencies alike.", source: { label: "Google Search Central — Do you need an SEO?", href: "https://developers.google.com/search/docs/fundamentals/do-i-need-seo" } },
      { type: "h2", text: "Questions to ask before hiring" },
      { type: "steps", items: [
        "Who, by name, will work on my project every week?",
        "What will you review first, and how do you decide priorities?",
        "What deliverables will I receive, and how often?",
        "How is progress measured, and which indicators go beyond traffic?",
        "What do you need from my team to keep work moving?",
        "What happens to access, data and completed work if we part ways?",
      ]},
      { type: "h2", text: "Red flags in any provider" },
      { type: "list", tone: "alert", items: [
        "Guarantees of rankings, traffic or sales.",
        "Identical proposals for companies in different situations.",
        "No clarity on who actually does the work.",
        "Reports built on metrics disconnected from the business.",
      ]},
      { type: "h2", text: "A combined model is also valid" },
      { type: "p", text: "In many companies it works for a consultant to set direction and oversee, while an in-house team or agency executes. Strategy then doesn't depend on whoever produces, and production has clear judgment behind it." },
    ],
    cta: {
      text: "If you're unsure which model your company needs, the free 30-minute initial consultation is for understanding your situation and defining possible next steps.",
      label: "Request free 30-minute initial consultation",
      href: "/en/free-seo-diagnosis",
    },
    related: [
      { label: "Strategic SEO consultant", href: "/en/seo-consultant" },
      { label: "SEO audit or SEO strategy?", href: "/en/library/seo-audit-vs-seo-strategy" },
      { label: "Methodology", href: "/en/methodology" },
    ],
  },
};
